import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ThemeContext } from '../contexts/AppContext';
import { Header, Footer } from '../components/Layout';

// Application Card Component
const ApplicationCard = ({ title, description, useCases, benefits, index }) => {
  const { isDarkMode } = React.useContext(ThemeContext);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const textColorClass = isDarkMode ? 'text-white' : 'text-black';
  const mutedTextColorClass = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBgColorClass = isDarkMode ? 'bg-white/3' : 'bg-black/3';
  const borderColorClass = isDarkMode ? 'border-white/10' : 'border-black/10';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ 
        duration: 0.8, 
        delay: index * 0.2,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      whileHover={{ 
        y: -8, 
        scale: 1.02,
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      className={`${cardBgColorClass} backdrop-blur-xl border ${borderColorClass} rounded-2xl p-6 transition-all duration-500 overflow-hidden`}
    >
      <motion.h3 
        className={`text-xl font-bold ${textColorClass} font-mono mb-4`}
        initial={{ x: -20, opacity: 0 }}
        animate={isInView ? { x: 0, opacity: 1 } : { x: -20, opacity: 0 }}
        transition={{ delay: index * 0.2 + 0.2, duration: 0.5 }}
      >
        {title}
      </motion.h3>
      
      <motion.p 
        className={`${mutedTextColorClass} mb-4 leading-relaxed font-mono text-sm`}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ delay: index * 0.2 + 0.3, duration: 0.5 }}
      >
        {description}
      </motion.p>
      
      <motion.div 
        className="mb-4"
        initial={{ y: 20, opacity: 0 }}
        animate={isInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
        transition={{ delay: index * 0.2 + 0.4, duration: 0.5 }}
      >
        <h4 className={`text-sm font-semibold ${textColorClass} font-mono mb-2`}>Real-World Applications:</h4>
        <ul className="space-y-1">
          {useCases.map((useCase, useCaseIndex) => (
            <li key={useCaseIndex} className={`${mutedTextColorClass} font-mono text-xs flex items-start`}>
              <span className={`mr-2 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>•</span>
              {useCase}
            </li>
          ))}
        </ul>
      </motion.div>
      
      <motion.div 
        initial={{ y: 20, opacity: 0 }}
        animate={isInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
        transition={{ delay: index * 0.2 + 0.5, duration: 0.5 }}
      >
        <h4 className={`text-sm font-semibold ${textColorClass} font-mono mb-2`}>Key Benefits:</h4>
        <ul className="space-y-1">
          {benefits.map((benefit, benefitIndex) => (
            <li key={benefitIndex} className={`${mutedTextColorClass} font-mono text-xs flex items-start`}>
              <span className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>•</span>
              {benefit}
            </li>
          ))}
        </ul>
      </motion.div>
    </motion.div>
  );
};

// Main Innovation Lab Applications Page
const InnovationLabPage = () => {
  const { isDarkMode } = React.useContext(ThemeContext);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });
  
  const bgColorClass = isDarkMode ? 'bg-black' : 'bg-white';
  const textColorClass = isDarkMode ? 'text-white' : 'text-black';
  const mutedTextColorClass = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  
  // Parallax effects
  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);

  // Real-world applications for Innovation Lab
  const applications = [
    {
      title: "Marketing Innovation & Customer Experience",
      description: "Create breakthrough marketing solutions and customer experiences that adapt and evolve in real-time based on user behavior and preferences.",
      useCases: [
        "Develop adaptive marketing campaigns that learn and improve",
        "Create personalized customer journey experiences",
        "Build intelligent content generation systems",
        "Design predictive customer service solutions",
        "Prototype next-generation customer engagement tools"
      ],
      benefits: [
        "Increase customer engagement by 60-80%",
        "Reduce marketing costs through intelligent automation",
        "Improve customer satisfaction and retention",
        "Stay ahead of market trends and competition"
      ]
    },
    {
      title: "Business Process Innovation",
      description: "Transform traditional business operations through rapid prototyping and testing of innovative solutions that streamline workflows and boost productivity.",
      useCases: [
        "Rapidly prototype new business models and processes",
        "Test and validate innovative operational strategies",
        "Develop intelligent automation solutions",
        "Create adaptive workflow management systems",
        "Build predictive business intelligence tools"
      ],
      benefits: [
        "Reduce operational costs by 30-50%",
        "Accelerate business transformation initiatives",
        "Improve employee productivity and satisfaction",
        "Enable rapid adaptation to market changes"
      ]
    },
    {
      title: "Data Innovation & Research Acceleration",
      description: "Accelerate research and discovery through innovative data processing techniques and rapid prototyping of analytical solutions.",
      useCases: [
        "Develop novel data analysis methodologies",
        "Create rapid research prototype systems",
        "Build intelligent data discovery tools",
        "Design adaptive research frameworks",
        "Prototype next-generation analytical platforms"
      ],
      benefits: [
        "Accelerate research timelines by 60-70%",
        "Enable breakthrough discoveries and insights",
        "Improve research accuracy and reliability",
        "Facilitate interdisciplinary collaboration"
      ]
    },
    {
      title: "Product Innovation & Development",
      description: "Revolutionize product development through rapid prototyping, iterative testing, and innovative design methodologies that bring ideas to market faster.",
      useCases: [
        "Rapidly prototype and test new product concepts",
        "Develop intelligent product design systems",
        "Create adaptive manufacturing solutions",
        "Build predictive product performance models",
        "Design next-generation user interfaces"
      ],
      benefits: [
        "Reduce product development time by 50-60%",
        "Lower development costs through rapid iteration",
        "Increase product success rates and market fit",
        "Enable faster response to customer needs"
      ]
    },
    {
      title: "Community Innovation & Social Impact",
      description: "Foster innovation in community building and social impact through rapid prototyping of solutions that strengthen connections and drive positive change.",
      useCases: [
        "Develop innovative community engagement platforms",
        "Create adaptive social impact measurement tools",
        "Build intelligent community matching systems",
        "Design collaborative problem-solving frameworks",
        "Prototype next-generation social networking solutions"
      ],
      benefits: [
        "Strengthen community bonds and engagement",
        "Increase participation in social programs",
        "Improve outcomes for community initiatives",
        "Enable scalable social impact solutions"
      ]
    },
    {
      title: "Intelligence Innovation & Security",
      description: "Advance security and intelligence capabilities through rapid prototyping of innovative solutions that stay ahead of evolving threats and challenges.",
      useCases: [
        "Develop next-generation threat detection systems",
        "Create adaptive security response protocols",
        "Build intelligent surveillance and monitoring tools",
        "Design predictive security risk models",
        "Prototype innovative intelligence analysis platforms"
      ],
      benefits: [
        "Improve threat detection accuracy by 90-95%",
        "Reduce response time to security incidents",
        "Prevent costly security breaches and attacks",
        "Enhance overall safety and protection capabilities"
      ]
    },
    {
      title: "Governance Innovation & Public Services",
      description: "Transform government operations and public services through rapid prototyping of innovative solutions that improve efficiency and citizen satisfaction.",
      useCases: [
        "Develop innovative citizen service delivery systems",
        "Create adaptive policy implementation frameworks",
        "Build intelligent resource allocation tools",
        "Design predictive governance models",
        "Prototype next-generation public engagement platforms"
      ],
      benefits: [
        "Improve government efficiency by 50-60%",
        "Enhance citizen satisfaction with public services",
        "Reduce government waste and operational costs",
        "Enable data-driven governance and policy decisions"
      ]
    }
  ];

  return (
    <div ref={containerRef} className={`min-h-screen ${bgColorClass} relative overflow-hidden`}>
      <Header />
      {/* Animated background elements */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        style={{ y }}
      >
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 30% 30%, ${isDarkMode ? 'rgba(255,100,0,0.1)' : 'rgba(255,100,0,0.05)'} 0%, transparent 50%),
                          radial-gradient(circle at 70% 70%, ${isDarkMode ? 'rgba(255,0,100,0.1)' : 'rgba(255,0,100,0.05)'} 0%, transparent 50%)`,
          backgroundSize: '600px 600px'
        }}></div>
      </motion.div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24 relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.h1 
            className={`text-3xl md:text-4xl lg:text-5xl font-mono font-bold ${textColorClass} tracking-tight leading-tight mb-4`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Innovation Lab
            <br />
            <motion.span 
              className={`${textColorClass}`}
              animate={{ 
                opacity: [0.7, 1, 0.7],
                scale: [1, 1.02, 1]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              Applications
            </motion.span>
          </motion.h1>
          <motion.p 
            className={`max-w-3xl mx-auto text-base md:text-lg ${mutedTextColorClass} leading-relaxed font-mono`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Transform ideas into reality through rapid prototyping and innovative technology integration. 
            Our Innovation Lab accelerates the development of breakthrough solutions that solve real-world 
            problems and create lasting impact across industries and communities.
          </motion.p>
        </motion.div>

        {/* Applications Grid */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {applications.map((application, index) => (
              <ApplicationCard
                key={index}
                title={application.title}
                description={application.description}
                useCases={application.useCases}
                benefits={application.benefits}
                index={index}
              />
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.p 
            className={`${mutedTextColorClass} mb-6 font-mono text-sm`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Ready to turn your innovative ideas into reality?
          </motion.p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <motion.a 
              href="/demo" 
              className={`group relative ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} backdrop-blur-xl ${textColorClass} px-6 py-3 rounded-xl text-sm font-mono font-semibold transition-all duration-300 ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-black/10'} hover:shadow-2xl ${isDarkMode ? 'hover:shadow-white/10' : 'hover:shadow-black/10'} overflow-hidden border ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}
              whileHover={{ 
                scale: 1.05,
                y: -2,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <span className="relative z-10">Start Innovating</span>
              <motion.div 
                className={`absolute inset-0 ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
            </motion.a>
            <motion.a 
              href="/services" 
              className={`group relative bg-transparent border ${isDarkMode ? 'border-white/10' : 'border-black/10'} ${textColorClass} px-6 py-3 rounded-xl text-sm font-mono font-semibold transition-all duration-300 overflow-hidden`}
              whileHover={{ 
                scale: 1.05,
                y: -2,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <span className="relative z-10">View All Services</span>
              <motion.div 
                className={`absolute inset-0 ${isDarkMode ? 'bg-white/3' : 'bg-black/3'}`}
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
            </motion.a>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default InnovationLabPage;
